/**
 * Verwaltung: wer darf was, und wer kommt überhaupt herein.
 *
 * Drei Bereiche in einer Seite, weil sie zusammengehören: ein neuer
 * Mitarbeiter bekommt eine Einladung, wird danach zum Benutzer, und ein
 * Bauhof bekommt statt eines Benutzers ein Betreiberkonto. Wer die drei Wege
 * auf drei Seiten verteilt, sucht.
 *
 * Nur admin und inhaber kommen hierher – der Server prüft das ebenfalls, die
 * Oberfläche versteckt es zusätzlich, damit niemand auf eine 403 läuft.
 */

import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useKunden } from '../api/abfragen'
import { ROLLENTEXT, type Rolle } from '../api/typen'
import {
  useBenutzer,
  useBenutzerAendern,
  useEinladungAnlegen,
  useEinladungZuruecknehmen,
  useEinladungen,
  useRechtSetzen,
  useRechte,
  type BenutzerZeile,
  type EinladungAus,
} from '../api/verwaltung'
import { Ablagebereich } from './Ablagebereich'
import { Mailversand } from './Mailversand'
import { Leistungskatalog } from './Leistungskatalog'
import { Nummernkreise } from './Nummernkreise'
import { Referenzen } from './Referenzen'
import { Systembereich } from './Systembereich'
import { MicrosoftBereich } from './MicrosoftBereich'
import { Unternehmen } from './Unternehmen'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'
import { Inventur } from './Inventur'
import { Uebernahme } from './Uebernahme'
import { darfVerwalten, useKonto } from '../konto/KontoKontext'
import { useNavigate } from 'react-router-dom'

const ROLLEN: Rolle[] = ['admin', 'inhaber', 'pruefer', 'buero', 'betrachter']
/* Die Bereiche der Verwaltung, in Gruppen.

   Vorher war das eine Reihe gleich breiter Knöpfe. Bei acht Bereichen
   brach sie um, lange Beschriftungen wurden abgeschnitten, und die
   Reihenfolge sagte nichts – „Microsoft 365" stand neben „Nummern", ohne
   dass ein Zusammenhang erkennbar war.

   Jetzt gruppiert: wer Zugänge vergibt, sucht anderswo als wer eine
   Schnittstelle einrichtet. Die Gruppen sind auch der Platz, an dem
   Neues hinzukommt, ohne dass die Leiste weiter wächst. */
type Bereich =
  | 'benutzer'
  | 'einladungen'
  | 'briefkopf'
  | 'nummern'
  | 'referenzen'
  | 'leistungen'
  | 'microsoft'
  | 'ablage'
  | 'mailversand'
  | 'uebernahme'
  | 'inventur'
  | 'system'

type Eintrag = { wert: Bereich; text: string; hinweis: string }
type Gruppe = { titel: string; hinweis: string; bereiche: Eintrag[] }

const GRUPPEN: Gruppe[] = [
  {
    titel: 'Zugang',
    hinweis: 'Wer im Prüfbüro arbeitet',
    bereiche: [
      { wert: 'benutzer', text: 'Benutzer', hinweis: 'Mitarbeiter des Prüfbüros' },
      { wert: 'einladungen', text: 'Einladungen', hinweis: 'Offene Einladungen' },
    ],
  },
  {
    titel: 'Unternehmen',
    hinweis: 'Was auf Dokumenten und der öffentlichen Seite steht',
    bereiche: [
      { wert: 'briefkopf', text: 'Unternehmen', hinweis: 'Briefkopf, Bankdaten, Logo' },
      {
        wert: 'nummern',
        text: 'Nummernkreise',
        hinweis: 'Kunden-, Auftrags-, Berichtsnummern',
      },
      {
        wert: 'referenzen',
        text: 'Referenzen',
        hinweis: 'Kundenstimmen auf der Terminseite',
      },
      {
        wert: 'leistungen',
        text: 'Leistungen & Vorlagen',
        hinweis: 'Preise und Angebotsmuster',
      },
    ],
  },
  {
    titel: 'Anbindungen',
    hinweis: 'Verbindungen nach außen',
    bereiche: [
      { wert: 'microsoft', text: 'Microsoft 365', hinweis: 'Anmeldung und Mailversand' },
      { wert: 'ablage', text: 'Dokumentenablage', hinweis: 'SharePoint, Nextcloud, S3' },
      { wert: 'mailversand', text: 'Mailversand', hinweis: 'Betriebsart und Postausgang' },
    ],
  },
  {
    titel: 'Daten',
    hinweis: 'Bestand hereinholen',
    bereiche: [
      {
        wert: 'inventur',
        text: 'Ablage ansehen',
        hinweis: 'Was liegt im Ordnerbaum? Zählt nur',
      },
      {
        wert: 'uebernahme',
        text: 'Bestand übernehmen',
        hinweis: 'Kunden, Plätze, Geräte aus einer Datei',
      },
    ],
  },
  {
    titel: 'System',
    hinweis: 'Betrieb der Anwendung',
    bereiche: [
      {
        wert: 'system',
        text: 'Sicherung & Update',
        hinweis: 'Abzüge, Fassung, Rückspielung',
      },
    ],
  },
]

const ALLE: Eintrag[] = GRUPPEN.flatMap((g) => g.bereiche)

export function Verwaltung() {
  const { konto } = useKonto()
  const [bereich, setzeBereich] = useState<Bereich>('benutzer')

  if (!darfVerwalten(konto)) return <Navigate to="/" replace />

  const jetziger = ALLE.find((b) => b.wert === bereich)

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Verwaltung</h1>

      {/* Auf dem Handy eine Auswahlliste: eine senkrechte Navigation
          schöbe den Inhalt jedes Mal nach unten aus dem Blick. */}
      <div className="md:hidden">
        <label htmlFor="v-bereich">Bereich</label>
        <select
          id="v-bereich"
          value={bereich}
          onChange={(e) => setzeBereich(e.target.value as Bereich)}
        >
          {GRUPPEN.map((g) => (
            <optgroup key={g.titel} label={g.titel}>
              {g.bereiche.map((b) => (
                <option key={b.wert} value={b.wert}>
                  {b.text}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div className="md:grid md:grid-cols-[16rem_1fr] md:gap-6">
        <nav
          aria-label="Bereiche der Verwaltung"
          className="hidden md:block"
        >
          <ul className="space-y-4">
            {GRUPPEN.map((g) => (
              <li key={g.titel}>
                <div className="mb-1 text-sm font-bold uppercase tracking-wide text-slate-500">
                  {g.titel}
                </div>
                <ul className="space-y-1">
                  {g.bereiche.map((b) => (
                    <li key={b.wert}>
                      <button
                        type="button"
                        aria-current={bereich === b.wert ? 'page' : undefined}
                        onClick={() => setzeBereich(b.wert)}
                        className={`w-full rounded-feld px-3 py-2 text-left ${
                          bereich === b.wert
                            ? 'bg-akzent-grund font-semibold text-akzent-700 ring-2 ring-akzent-500'
                            : 'hover:bg-slate-100'
                        }`}
                      >
                        <span className="block">{b.text}</span>
                        <span className="block text-base font-normal text-slate-600">
                          {b.hinweis}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 space-y-4">
          {/* Die Überschrift des Bereichs: auf dem Handy ist die Auswahlliste
              oben weggescrollt, sobald man liest. */}
          <h2 className="text-xl font-bold md:sr-only">{jetziger?.text}</h2>

          {bereich === 'inventur' && <Inventur />}
          {bereich === 'uebernahme' && <Uebernahme />}
          {bereich === 'benutzer' && <Benutzerbereich />}
          {bereich === 'einladungen' && <Einladungsbereich />}
          {bereich === 'briefkopf' && <Unternehmen />}
          {bereich === 'microsoft' && <MicrosoftBereich />}
          {bereich === 'ablage' && <Ablagebereich />}
          {bereich === 'nummern' && <Nummernkreise />}
          {bereich === 'referenzen' && <Referenzen />}
          {bereich === 'leistungen' && <Leistungskatalog />}
          {bereich === 'mailversand' && <Mailversand />}
          {bereich === 'system' && <Systembereich />}
        </div>
      </div>
    </div>
  )
}

// ── Benutzer ─────────────────────────────────────────────────────

function Benutzerbereich() {
  const { konto } = useKonto()
  const liste = useBenutzer()
  const [bearbeitet, setzeBearbeitet] = useState<BenutzerZeile | null>(null)

  if (liste.isLoading) return <Ladeanzeige />
  if (liste.error) return <Fehlerhinweis fehler={liste.error} />

  const benutzer = liste.data ?? []

  return (
    <>
      <ul className="space-y-2">
        {benutzer.map((b) => (
          <li key={b.id}>
            <button
              type="button"
              className="karte-knopf"
              onClick={() => setzeBearbeitet(b)}
            >
              <div className="flex-1">
                <div className="font-semibold">
                  {b.name}
                  {b.id === konto?.id && (
                    <span className="ml-2 text-base font-normal text-slate-600">(Sie)</span>
                  )}
                </div>
                <div className="text-base text-slate-700">{b.email}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <span className="marke bg-marke-100 text-marke-700">
                    {ROLLENTEXT[b.rolle]}
                  </span>
                  {!b.aktiv && (
                    <span className="marke bg-stufe-3-grund text-stufe-3">gesperrt</span>
                  )}
                  {b.kunden_ids.length > 0 && (
                    <span className="marke bg-slate-100 text-slate-700">
                      {b.kunden_ids.length} {b.kunden_ids.length === 1 ? 'Kunde' : 'Kunden'}
                    </span>
                  )}
                  {b.hat_microsoft && (
                    <span className="marke bg-slate-100 text-slate-700">Microsoft</span>
                  )}
                </div>
              </div>
              <div className="text-right text-sm text-slate-600">
                <div>zuletzt</div>
                <div>{datumDeutsch(b.letzte_anmeldung)}</div>
              </div>
            </button>
          </li>
        ))}
      </ul>

      {bearbeitet && (
        <BenutzerBlatt benutzer={bearbeitet} schliessen={() => setzeBearbeitet(null)} />
      )}
    </>
  )
}

function BenutzerBlatt({
  benutzer,
  schliessen,
}: {
  benutzer: BenutzerZeile
  schliessen: () => void
}) {
  const { konto, uebernehmen } = useKonto()
  const navigieren = useNavigate()
  const kunden = useKunden()
  const aendern = useBenutzerAendern()
  const [wechselt, setzeWechselt] = useState(false)
  const [wechselfehler, setzeWechselfehler] = useState<unknown>(null)

  const [rolle, setzeRolle] = useState<Rolle>(benutzer.rolle)
  const [aktiv, setzeAktiv] = useState(benutzer.aktiv)
  const [eingeschraenkt, setzeEingeschraenkt] = useState(benutzer.kunden_ids.length > 0)
  const [kundenIds, setzeKundenIds] = useState<string[]>(benutzer.kunden_ids)

  const eigenes = benutzer.id === konto?.id

  function speichern() {
    aendern.mutate(
      {
        id: benutzer.id,
        rolle,
        aktiv,
        // Ohne Einschränkung eine leere Liste schicken: der Server versteht
        // das als „alle Kunden", nicht als „keine".
        kunden_ids: eingeschraenkt ? kundenIds : [],
      },
      { onSuccess: schliessen },
    )
  }

  return (
    <Blatt
      titel={benutzer.name}
      untertitel={benutzer.email}
      schliessen={schliessen}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf-haupt flex-1"
            onClick={speichern}
            disabled={aendern.isPending}
          >
            {aendern.isPending ? 'Speichert …' : 'Speichern'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {eigenes && (
          <p className="rounded-feld border-2 border-marke-200 bg-marke-50 p-3 text-marke-700">
            Das ist Ihr eigenes Konto. Sperren und Herabstufen sind hier gesperrt –
            sonst stünde am Ende niemand mehr zur Verfügung, der Rollen vergibt.
          </p>
        )}

        <div>
          <label htmlFor="b-rolle">Rolle</label>
          <select
            id="b-rolle"
            value={rolle}
            onChange={(e) => setzeRolle(e.target.value as Rolle)}
            disabled={eigenes}
          >
            {ROLLEN.map((r) => (
              <option key={r} value={r}>
                {ROLLENTEXT[r]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-base text-slate-600">{rollenhinweis(rolle)}</p>
        </div>

        {benutzer.systemverwalter && (
          /* Damit es nicht unerklärt fehlt.

             Dieses Konto reicht über das eigene Prüfbüro hinaus. Es hier
             zu verschweigen hieße: der Inhaber sieht ein Konto mit einem
             Bereich, den er nicht kennt, und findet nirgends heraus,
             warum. Ändern lässt es sich hier bewusst nicht – sonst
             könnte sich die Verwaltung eines Büros selbst über den
             eigenen Mandanten hinausheben. */
          <div className="rounded-feld border-2 border-marke-200 bg-marke-50 p-3">
            <p className="font-semibold text-marke-700">
              Systemverwalter der Anwendung
            </p>
            <p className="mt-1 text-base text-marke-700">
              Dieses Konto betreut die Anwendung selbst und kann weitere
              Prüfbüros anlegen. Das ist keine Einstellung Ihres Büros und
              lässt sich hier nicht ändern — es wird vom Betreiber der
              Anwendung auf dem Server vergeben.
            </p>
          </div>
        )}

        <Rechtematrix benutzer={benutzer} />

        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={aktiv}
            onChange={(e) => setzeAktiv(e.target.checked)}
            disabled={eigenes}
            className="mt-1 h-6 w-6"
          />
          <span className="font-normal">
            <span className="font-semibold">Konto aktiv</span>
            <span className="block text-base text-slate-600">
              Beim Sperren werden die offenen Sitzungen sofort beendet.
            </span>
          </span>
        </label>

        <div>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={eingeschraenkt}
              onChange={(e) => setzeEingeschraenkt(e.target.checked)}
              className="mt-1 h-6 w-6"
            />
            <span className="font-normal">
              <span className="font-semibold">Nur bestimmte Kunden</span>
              <span className="block text-base text-slate-600">
                Ohne Häkchen sieht dieses Konto alle Kunden.
              </span>
            </span>
          </label>

          {eingeschraenkt && (
            <div className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-feld border-2 border-slate-200 p-2">
              {kunden.isLoading && <Ladeanzeige />}
              {(kunden.data ?? []).map((k) => (
                <label key={k.id} className="flex items-center gap-3 py-1">
                  <input
                    type="checkbox"
                    checked={kundenIds.includes(k.id)}
                    onChange={(e) =>
                      setzeKundenIds((vorher) =>
                        e.target.checked
                          ? [...vorher, k.id]
                          : vorher.filter((id) => id !== k.id),
                      )
                    }
                    className="h-6 w-6"
                  />
                  <span className="font-normal">{k.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {!benutzer.hat_passwort && (
          <p className="text-base text-slate-600">
            Dieses Konto hat kein Passwort und meldet sich über Microsoft 365 an.
          </p>
        )}

        <Fehlerhinweis fehler={aendern.error} />

        {/* Der Weg nachzusehen, was jemand anderes sieht.

            Die Administration war hier ausgenommen, mit der Begründung, sie
            dürfe ohnehin nicht prüfen. Die Begründung gilt nicht mehr — sie
            darf es —, also gilt auch die Ausnahme nicht mehr. Wer Benutzer
            verwaltet, muss in jedes Konto seines Büros hineinsehen können,
            sonst lässt sich eine Rückmeldung wie „bei mir fehlt der Knopf"
            nicht nachvollziehen. */}
        {!eigenes && benutzer.aktiv && (
          <div className="border-t-2 border-slate-200 pt-4">
            <button
              type="button"
              className="knopf-zweit w-full"
              disabled={wechselt}
              onClick={() => {
                setzeWechselt(true)
                setzeWechselfehler(null)
                void uebernehmen(benutzer.id)
                  .then(() => navigieren('/'))
                  // Ohne das blieb ein abgelehnter Wechsel unsichtbar: der
                  // Knopf sprang zurück und sonst geschah nichts.
                  .catch((fehler: unknown) => setzeWechselfehler(fehler))
                  .finally(() => setzeWechselt(false))
              }}
            >
              {wechselt ? 'Wechselt …' : `Ansicht von ${benutzer.name} übernehmen`}
            </button>
            <p className="mt-1 text-base text-slate-600">
              Sie sehen und tun dann alles als diese Person – mit vollen Rechten,
              einschließlich Prüfungen abschließen. Der Vorgang wird dauerhaft
              festgehalten. Zurück geht es über den Streifen am oberen Rand.
            </p>
            <div className="mt-2">
              <Fehlerhinweis fehler={wechselfehler} />
            </div>
          </div>
        )}
      </div>
    </Blatt>
  )
}

function rollenhinweis(rolle: Rolle): string {
  switch (rolle) {
    case 'admin':
      return 'Alles, einschließlich Benutzerverwaltung.'
    case 'inhaber':
      return 'Alles im Fachbetrieb, einschließlich Benutzerverwaltung.'
    case 'pruefer':
      return 'Darf prüfen, beurteilen und Berichte abschließen.'
    case 'buero':
      return 'Darf Stammdaten und Termine pflegen, aber nicht beurteilen.'
    case 'betrachter':
      return 'Darf nur lesen.'
  }
}

// ── Einladungen ──────────────────────────────────────────────────

function Einladungsbereich() {
  const liste = useEinladungen()
  const anlegen = useEinladungAnlegen()
  const zuruecknehmen = useEinladungZuruecknehmen()
  const [neu, setzeNeu] = useState<EinladungAus | null>(null)
  const [kopiert, setzeKopiert] = useState(false)

  return (
    <div className="space-y-4">
      <form
        className="karte space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          const formular = new FormData(e.currentTarget)
          const feld = e.currentTarget
          anlegen.mutate(
            {
              email: String(formular.get('email')),
              name: String(formular.get('name')),
              rolle: String(formular.get('rolle')) as Rolle,
            },
            {
              onSuccess: (antwort) => {
                setzeNeu(antwort)
                setzeKopiert(false)
                feld.reset()
              },
            },
          )
        }}
      >
        <h2 className="text-xl font-bold">Jemanden einladen</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="e-name">Name</label>
            <input id="e-name" name="name" required maxLength={120} />
          </div>
          <div>
            <label htmlFor="e-email">E-Mail</label>
            <input id="e-email" name="email" type="email" required autoCapitalize="none" />
          </div>
          <div>
            <label htmlFor="e-rolle">Rolle</label>
            <select id="e-rolle" name="rolle" defaultValue="pruefer">
              {ROLLEN.map((r) => (
                <option key={r} value={r}>
                  {ROLLENTEXT[r]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <Fehlerhinweis fehler={anlegen.error} />

        <button type="submit" className="knopf-haupt" disabled={anlegen.isPending}>
          {anlegen.isPending ? 'Legt an …' : 'Einladung erzeugen'}
        </button>
      </form>

      {neu?.einladungslink && (
        <Blatt
          titel="Einladung erzeugt"
          untertitel={`${neu.name} · ${neu.email}`}
          schliessen={() => setzeNeu(null)}
          fuss={
            <button type="button" className="knopf-haupt w-full" onClick={() => setzeNeu(null)}>
              Fertig
            </button>
          }
        >
          <div className="space-y-3">
            <p>
              Diesen Link weitergeben. Er gilt 72 Stunden und lässt sich nur einmal
              einlösen. Er steht <span className="font-semibold">nur jetzt</span> hier –
              danach existiert er nirgends mehr im Klartext.
            </p>
            <textarea
              readOnly
              value={neu.einladungslink}
              rows={3}
              aria-label="Einladungslink"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              className="knopf-zweit w-full"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(neu.einladungslink ?? '')
                  .then(() => setzeKopiert(true))
              }}
            >
              {kopiert ? 'Kopiert' : 'Link kopieren'}
            </button>
          </div>
        </Blatt>
      )}

      <h2 className="text-xl font-bold">Offene Einladungen</h2>

      {liste.isLoading ? (
        <Ladeanzeige />
      ) : liste.error ? (
        <Fehlerhinweis fehler={liste.error} />
      ) : (liste.data ?? []).length === 0 ? (
        <Leerhinweis
          titel="Keine offenen Einladungen"
          text="Eingelöste Einladungen werden hier nicht mehr geführt."
        />
      ) : (
        <ul className="space-y-2">
          {(liste.data ?? []).map((e) => (
            <li key={e.id} className="karte flex items-center gap-3">
              <div className="flex-1">
                <div className="font-semibold">{e.name}</div>
                <div className="text-base text-slate-700">{e.email}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <span className="marke bg-marke-100 text-marke-700">
                    {ROLLENTEXT[e.rolle]}
                  </span>
                  <span className="marke bg-slate-100 text-slate-700">
                    gültig bis {datumDeutsch(e.laeuft_ab_am)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="knopf-warnung px-3"
                onClick={() => zuruecknehmen.mutate(e.id)}
                disabled={zuruecknehmen.isPending}
              >
                Zurücknehmen
              </button>
            </li>
          ))}
        </ul>
      )}

      <Fehlerhinweis fehler={zuruecknehmen.error} />
    </div>
  )
}

// ── Betreiberkonten ──────────────────────────────────────────────

function Rechtematrix({ benutzer }: { benutzer: BenutzerZeile }) {
  const rechte = useRechte(benutzer.id)
  const setzen = useRechtSetzen(benutzer.id)

  if (rechte.isLoading) return <Ladeanzeige />
  if (rechte.error) return <Fehlerhinweis fehler={rechte.error} />

  const stand = rechte.data!

  /* Auch beim Administrator wird die Liste gezeigt, nur gesperrt.
     Vorher stand hier bloß ein Satz — und wer wissen wollte, was dieses
     Konto eigentlich darf, erfuhr es nirgends. Etwas zu verbergen, weil es
     sich nicht ändern lässt, sind zwei verschiedene Dinge. */
  const gesperrt = stand.unveraenderlich

  return (
    <div>
      <div className="mb-1 font-semibold text-slate-800">Rechte</div>
      <p className="mb-2 text-base text-slate-600">
        {gesperrt
          ? 'Die Administration hat sämtliche Rechte, und sie lassen sich ihr nicht entziehen — sonst könnte sich jemand selbst aussperren. Um daran etwas zu ändern, wählen Sie oben eine andere Rolle.'
          : 'Angehakt ist, was diese Person darf. Was aus der Rolle kommt, wandert beim Rollenwechsel mit; was hier einzeln gesetzt wurde, bleibt.'}
      </p>

      <Fehlerhinweis fehler={setzen.error} />

      <ul className="space-y-1">
        {stand.rechte.map((r) => (
          <li
            key={r.recht}
            className={`rounded-feld p-2 ${r.abweichung ? 'bg-akzent-grund' : ''}`}
          >
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-6 w-6 shrink-0"
                checked={r.hat}
                disabled={gesperrt || setzen.isPending}
                onChange={(e) =>
                  setzen.mutate({ recht: r.recht, gewaehrt: e.target.checked })
                }
              />
              <span className="min-w-0 flex-1 font-normal">
                <span className={r.hat ? 'font-semibold' : ''}>{r.text}</span>
                <span className="block text-base text-slate-600">
                  {r.abweichung
                    ? r.hat
                      ? 'Einzeln erteilt, kommt nicht aus der Rolle'
                      : 'Einzeln entzogen, obwohl die Rolle es mitbringt'
                    : r.aus_der_rolle
                      ? 'Aus der Rolle'
                      : 'Bringt die Rolle nicht mit'}
                </span>
              </span>
              {r.abweichung && !gesperrt && (
                <button
                  type="button"
                  className="knopf-still shrink-0 px-2 text-base"
                  disabled={setzen.isPending}
                  onClick={(e) => {
                    // Sonst schaltet der Klick zusätzlich das Kästchen um.
                    e.preventDefault()
                    setzen.mutate({ recht: r.recht, gewaehrt: null })
                  }}
                >
                  Zurücksetzen
                </button>
              )}
            </label>
          </li>
        ))}
      </ul>
    </div>
  )
}
